<template>
  <CollapsibleSection section-key="serving" class="serving-section" @toggle="handleToggle">
    <template #title>
      <span>Serving Information</span>
    </template>
    <div class="field">
      <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
        <label for="label-name-input" class="label is-small-mobile mb-0">Label Name</label>
        <label class="checkbox is-small-mobile is-size-7-tablet-only">
          <input
            v-model="store.hideName"
            type="checkbox"
            class="mr-1"
            data-test-id="hide-name-checkbox"
          />
          Hide
          <IconHint
            content="This just hides the recipe title when you're printing/rendering. It doesn't affect the label at all, just what you see when you print it to PDF."
          />
        </label>
      </div>
      <div class="control">
        <input
          id="label-name-input"
          v-model="store.name"
          class="input is-small-mobile"
          type="text"
          placeholder="e.g., Daily Vitamin Complex"
          data-test-id="label-name-input"
        />
      </div>
    </div>

    <div class="field">
      <label
        for="serving-size-input"
        class="label is-small-mobile is-flex is-justify-content-space-between is-align-items-center"
      >
        <span>Serving Size</span>
        <IconHint
          content="The serving size is the recommended amount to be taken at one time (e.g. 1 capsule, 2 tablets, 1 scoop).<br><br>All nutrient values in the Supplement Facts panel must be based on this serving size."
        />
      </label>
      <div class="control">
        <input
          id="serving-size-input"
          v-model="store.servingSizeText"
          class="input is-small-mobile"
          type="text"
          placeholder="e.g., 1 capsule, 2 tablets, 1 scoop (10g)"
          data-test-id="serving-size-input"
        />
      </div>
    </div>

    <div class="field">
      <label
        for="servings-per-container-input"
        class="label is-small-mobile is-flex is-justify-content-space-between is-align-items-center"
      >
        <span>Servings Per Container</span>
        <IconHint
          content="This is the number of servings that EACH PACKAGE of your product has.<br><br>If each package has 20 capsules and a serving is 1 capsule, then there are 20 servings per package. If your recipe makes 2 packages and each package will have 10 capsules in it, then there are 10 servings per package.<br><br>If you put in a non-round number (e.g. 2.2), the label will round it and add the word 'about' to the servings per container statement."
        />
      </label>
      <div class="control">
        <input
          id="servings-per-container-input"
          v-model="store.servingsPerContainer"
          class="input is-small-mobile servings-per-container-input"
          type="number"
          inputmode="decimal"
          min="0"
          step="any"
          autocomplete="off"
          placeholder="e.g., 30"
          @keydown="preventScientificNotation"
        />
      </div>
    </div>

    <div class="field mb-0">
      <div class="control">
        <label class="checkbox">
          <input v-model="store.hideServingsPerContainer" type="checkbox" class="mr-2" />
          <span class="is-small-mobile">Hide Servings Per Container</span>
        </label>
      </div>
    </div>

    <div class="field">
      <div class="control">
        <label class="checkbox">
          <input v-model="store.singleRowLayout" type="checkbox" class="mr-2" />
          <span class="is-small-mobile">Single Row Layout</span>
        </label>
      </div>
    </div>

    <div class="field">
      <label
        for="amount-per-input"
        class="label is-small-mobile is-flex is-justify-content-space-between is-align-items-center"
      >
        <span>Amount Per ...</span>
        <IconHint
          content="Shown as 'Amount Per [text]' on label. You can type custom text or select from common options."
        />
      </label>
      <div class="control">
        <input
          id="amount-per-input"
          v-model="store.amountPerText"
          class="input is-small-mobile"
          type="text"
          placeholder="Serving"
          data-test-id="amount-per-input"
        />
      </div>
    </div>

    <div class="field">
      <label
        for="age-category-select"
        class="label is-small-mobile is-flex is-justify-content-space-between is-align-items-center"
      >
        <span>Age Category</span>
        <IconHint content="Daily Values are based on the selected age group" />
      </label>
      <div class="control">
        <div class="select is-small-mobile is-fullwidth">
          <select id="age-category-select" v-model="store.ageCategory">
            <option v-for="opt in store.ageCategoryOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>
      <p v-if="store.ageCategory === 'infant'" class="help has-text-info">
        <i class="fas fa-info-circle"></i>
        For products intended for infants under 7 months, %DV cannot be displayed per FDA
        regulations (21 CFR 101.36).
      </p>
    </div>

    <div class="field" style="margin-bottom: 6px">
      <label
        class="label is-small-mobile is-flex is-justify-content-space-between is-align-items-center"
      >
        <span>Additional Age Categories (Multi-Column)</span>
        <IconHint
          content="Select additional age categories to show multi-column %DV. Each selected category adds a %DV column."
        />
      </label>
      <div class="control">
        <label
          v-for="opt in additionalAgeCategoryOptions"
          :key="opt.value"
          class="checkbox is-block mb-1"
        >
          <input
            v-model="store.displayAgeCategories"
            class="mr-2"
            type="checkbox"
            :value="opt.value"
          />
          <span class="is-small-mobile">{{ opt.label }}</span>
        </label>
      </div>
    </div>

    <div v-if="store.formatOptions.length > 1" class="field">
      <label for="label-format-select" class="label is-small-mobile">Label Format</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select id="label-format-select" v-model="store.format">
            <option v-for="opt in store.formatOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </CollapsibleSection>
</template>

<script setup>
  import { computed, watch, inject } from "vue";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import CollapsibleSection from "./collapsible-section.vue";
  import IconHint from "../icon-hint.vue";

  import { singularize, parseServingSize } from "./serving-size-utils";
  import { preventScientificNotation } from "./utils/formatting";
  import { focusOnSectionOpen } from "./utils/focus-helpers";

  const store = useSupplementLabelStore();
  const triggerSave = inject("triggerSave");

  // Track if sync is happening to prevent loops
  let isSyncing = false;

  // When primary age category changes, remove it from displayAgeCategories if present
  watch(
    () => store.ageCategory,
    (newCategory) => {
      const idx = store.displayAgeCategories.indexOf(newCategory);
      if (idx >= 0) {
        store.displayAgeCategories.splice(idx, 1);
      }
    }
  );

  // Amount Per → Serving Size: auto-fill when Serving Size is blank
  // Only sync when amount per exactly matches a known option (not partial typing)
  watch(
    () => store.amountPerText,
    (newValue) => {
      if (isSyncing) return;
      // Check if serving size is completely empty or just whitespace
      if (newValue && (!store.servingSizeText || !store.servingSizeText.trim())) {
        const matchingOption = store.amountPerTextOptions.find(
          (opt) => opt.value.toLowerCase() === newValue.toLowerCase()
        );
        if (matchingOption) {
          isSyncing = true;
          store.servingSizeText = `1 ${matchingOption.value}`;
          triggerSave?.();
          setTimeout(() => {
            isSyncing = false;
          }, 0);
        }
      }
    }
  );

  // Serving Size → Amount Per: extract unit when Amount Per is blank
  // Only sync when serving size has both a number and a recognizable unit
  watch(
    () => store.servingSizeText,
    (newValue) => {
      if (isSyncing) return;
      if (newValue && !store.amountPerText) {
        const parsed = parseServingSize(newValue);
        if (parsed.number && parsed.unit) {
          const singular = singularize(parsed.unit);
          const matchingOption = store.amountPerTextOptions.find(
            (opt) => opt.value.toLowerCase() === singular.toLowerCase()
          );
          if (matchingOption) {
            isSyncing = true;
            store.amountPerText = matchingOption.value;
            triggerSave?.();
            setTimeout(() => {
              isSyncing = false;
            }, 0);
          }
        }
      }
    }
  );

  const additionalAgeCategoryOptions = computed(() => {
    return store.ageCategoryOptions.filter((opt) => opt.value !== store.ageCategory);
  });

  function handleToggle(isOpen, options = {}) {
    if (options.autoFocusFirstField) {
      focusOnSectionOpen(isOpen, ".serving-section input");
    }
  }
</script>
